import { useCallback, useEffect, useRef, useState, type DependencyList } from "react";
import { ApiError } from "./api-admin";

export type AsyncState<T> =
  | { status: "loading"; data: T | null }
  | { status: "error"; error: string; data: T | null }
  | { status: "ready"; data: T };

/** Runs `fn` whenever `deps` change; ignores stale responses; a 401 sends the user back to login. */
export function useAsync<T>(fn: () => Promise<T>, deps: DependencyList): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ status: "loading", data: null });
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let alive = true;
    setState((s) => ({ status: "loading", data: s.data }));
    fnRef.current().then(
      (data) => alive && setState({ status: "ready", data }),
      (e: unknown) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 401) {
          window.location.assign("/");
          return;
        }
        setState((s) => ({ status: "error", error: e instanceof Error ? e.message : "Onbekende fout", data: s.data }));
      },
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}
